import { queryClient } from "@/config/query"
import { type StateCreator, create } from "zustand"
import { persist } from "zustand/middleware"
import type { LoginResponse } from "@/services/api/cronos/auth/login/login"
import { UserSex, UserRole, type User } from "@/shared/entities/users"
import updateClientToken from "../helpers/update-client-token"
import type { ProductTourStatus } from "@/services/api/cronos/user/info/get"

interface AuthState {
  token: string
  partialToken: string
  user: User
  isAuthenticated: boolean
  logIn: (data: LoginResponse["description"]) => void
  logOut: () => void
  setWasOnboarded: () => void
  setPartialToken: (token: string) => void
  setProductTourStatus: (status: ProductTourStatus) => void
}

const authState: StateCreator<AuthState> = (set) => ({
  user: {
    id: "",
    firstName: "",
    lastName: "",
    universityId: "",
    externalId: "",
    role: UserRole.STUDENT,
    dailyStudyHours: 0,
    profilePicUrl: "",
    emailAddress: "",
    sex: UserSex.M,
    timeZone: "",
    wasOnboarded: false,
    productTour: null,
  },
  token: "",
  partialToken: "",
  isAuthenticated: false,
  logIn: ({ token, user }: LoginResponse["description"]) => {
    updateClientToken(token)
    set({ token, user, isAuthenticated: true })
  },
  logOut: () => {
    updateClientToken("")
    queryClient.clear()
    set({ token: "", user: undefined, isAuthenticated: false })
  },
  setWasOnboarded: () => {
    set((state) => ({
      user: { ...state.user, wasOnboarded: true },
    }))
  },
  setProductTourStatus: (status: ProductTourStatus) => {
    set((state) => ({
      user: { ...state.user, productTour: status },
    }))
  },
  setPartialToken: (token: string) => {
    set({ partialToken: token })
  },
})

export const useAuth = create<AuthState>()(
  persist(authState, {
    name: "@tuni/auth",
    partialize: (state) => ({
      user: state.user,
      token: state.token,
      isAuthenticated: state.isAuthenticated,
      partialToken: state.partialToken,
    }),
    onRehydrateStorage: () => (state) => {
      if (!state?.token) return
      updateClientToken(state.token)
      state.isAuthenticated = true
    },
  }),
)

export default useAuth.getState()
